/** One bounded report per iframe document; observers never own authentication or request guards. */
import { startupRoutes, type StartupRequestTiming, type StartupTiming } from '../src/shared/startup-timing-contract'

let state:
  | {
      report: Omit<StartupTiming, 'content' | 'contentCommittedAt' | 'reportedAt' | 'completion'>
      committed?: {
        content: StartupTiming['content']
        contentCommittedAt: number
        deliver: (report: StartupTiming) => void
      }
      active: number
      sent: boolean
      quietTimer?: ReturnType<typeof setTimeout>
      deadlineTimer: ReturnType<typeof setTimeout>
    }
  | undefined

const now = () => Math.round(performance.now())

/** Called by the iframe's JS entry, not a render or the host frame. No extra requests. */
export function startShopifyStartupTiming() {
  if (state || typeof window === 'undefined') return
  state = {
    report: {
      version: 1,
      navigationId: crypto.randomUUID(),
      navigationStartedAt: 0,
      ...(import.meta.env.VITE_CLIENT_BUILD ? { clientBuild: import.meta.env.VITE_CLIENT_BUILD } : {}),
      jsStartedAt: now(),
      droppedRequests: 0,
      requests: [],
    },
    active: 0,
    sent: false,
    deadlineTimer: setTimeout(() => finish('deadline'), 120_000),
  }
}

function finish(completion: StartupTiming['completion']) {
  if (!state || state.sent) return
  state.sent = true
  clearTimeout(state.quietTimer)
  clearTimeout(state.deadlineTimer)
  // No usable authenticated commit means there is no safe scope for a report.
  if (!state.committed) return
  const { content, contentCommittedAt, deliver } = state.committed
  deliver({ ...state.report, content, contentCommittedAt, reportedAt: now(), completion })
}

function scheduleReport() {
  if (!state || state.sent) return
  clearTimeout(state.quietTimer)
  if (state.committed && !state.active) state.quietTimer = setTimeout(() => finish('settled'), 500)
}

export function markShopifyStartupPhase(
  phase: 'bridgeWaitStartedAt' | 'bridgeReadyAt' | 'bootstrapParseStartedAt' | 'bootstrapParsedAt',
) {
  if (state && !state.sent && state.report[phase] === undefined) state.report[phase] = now()
}

/** The caller is a layout effect: discarded renders and StrictMode cannot emit duplicate reports. */
export function commitShopifyStartup(content: StartupTiming['content'], deliver: (report: StartupTiming) => void) {
  if (!state || state.sent || state.committed) return
  state.committed = { content, contentCommittedAt: now(), deliver }
  scheduleReport()
}

/** Observe only the fixed same-origin read inventory. Raw URLs/queries never enter the report. */
export function observeShopifyStartupRequest(input: RequestInfo | URL, method: string) {
  if (!state || state.sent || method.toUpperCase() !== 'GET') return
  const url = new URL(input instanceof Request ? input.url : String(input), window.location.origin)
  const route = startupRoutes.find((candidate) => candidate === url.pathname)
  if (!route || url.origin !== window.location.origin) return
  if (state.report.requests.length >= 12) {
    state.report.droppedRequests = Math.min(10_000, state.report.droppedRequests + 1)
    return
  }
  const owner = state
  const timing: StartupRequestTiming = { route, startedAt: now(), outcome: 'pending' }
  owner.report.requests.push(timing)
  owner.active++
  clearTimeout(owner.quietTimer)
  let ended = false
  return {
    dispatched() {
      if (!owner.sent) timing.dispatchedAt = now()
    },
    responded(response: Response) {
      if (owner.sent) return
      timing.respondedAt = now()
      timing.status = response.status
      const correlationId = response.headers.get('X-Correlation-Id')
      if (correlationId && /^[a-zA-Z0-9_-]{1,64}$/.test(correlationId)) timing.correlationId = correlationId
    },
    settled(outcome: StartupRequestTiming['outcome']) {
      if (ended) return
      ended = true
      owner.active--
      if (!owner.sent) timing.outcome = outcome
      scheduleReport()
    },
  }
}
